import clsx from 'clsx';
import { useState } from 'react';
import { SvgIcon } from '../../../../common/components/SvgIcon';
import { useSgwtTranslation } from '../../../../common/i18n';
import { useBotConversation } from '../../shared/BotConversationContext';
import type { ConversationMessage, SgwtChatbotMessage } from '../../shared/chatbot.types';
import { AIIcon } from '../layout/AIIcon';
import { CopyButton } from '../layout/CopyButton';
import { MessageContent } from '../MessageContent';

type MessageProps = {
  record: ConversationMessage;
};

type SourcesProps = {
  message: SgwtChatbotMessage;
};

const MessageHeader = ({ message }: { message: SgwtChatbotMessage }) => {
  const { translate } = useSgwtTranslation();
  if (message.role !== 'bot') {
    return null;
  }
  return (
    <div className="sgwt-chatbot-message-header">
      <AIIcon />
      <span className="badge badge-discreet-secondary rounded-pill mx-2">{translate('ai.generated')}</span>
    </div>
  );
};

const MessageSources = ({ message }: SourcesProps) => {
  const [showSources, setShowSources] = useState(false);
  const { translate } = useSgwtTranslation();

  if (!message.sources) {
    return null;
  }

  const collapseId = `collapse-sources-${message.id}`;
  return (
    <div className="sgwt-chatbot-message-sources">
      <div className="collapse-divider">
        <button
          className={clsx('btn btn-collapse collapse-fill-center collapse-icon-end text-secondary', !showSources && 'collapsed')}
          type="button"
          onClick={() => setShowSources(!showSources)}
          data-bs-toggle="collapse"
          data-bs-target={`#${collapseId}`}
          aria-expanded={showSources}
          aria-controls={collapseId}
        >
          {translate('sources')}
        </button>
      </div>
      <div className={clsx('collapse ', showSources && 'show')} id={collapseId}>
        <MessageContent className="mb-3" message={message.sources} />
      </div>
    </div>
  );
};

export const OneMessage = ({ record }: MessageProps) => {
  const message = record.content;
  const { role } = message;
  const { addReaction, feedbackContactUs, setInputText } = useBotConversation();
  const [showActions, setShowActions] = useState(false);
  const { translate } = useSgwtTranslation();
  const botMessage = role === 'bot';

  const copyToInput = () => {
    const txt = message.message.content;
    setInputText(txt);
  };

  const like = () => addReaction(message, 'like');
  const dislike = () => addReaction(message, 'dislike');
  const contactSupport = () => feedbackContactUs(true);

  const hasSources = message.sources?.content;

  const contentToCopy = message.message.content + (hasSources ? `\n\nSources:\n${message.sources?.content}` : '');

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: OK
    <section
      className={clsx(`sgwt-chatbot-message sgwt-chatbot-message-${role} message-${message.id ?? '000'}`, showActions ? 'mb-3' : 'mb-5')}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {/* Message header  */}
      <MessageHeader message={message} />
      {/* Message content */}
      <MessageContent
        message={message.message}
        className={clsx('sgwt-chatbot-message-content', botMessage ? '' : 'bg-primary p-12px d-flex justify-content-start text-break')}
      />
      <MessageSources message={message} />
      {/* Actions */}
      {botMessage ? (
        <div className="sgwt-chatbot-message-actions d-flex justify-content-between">
          {showActions && (
            <>
              <div>
                <CopyButton text={contentToCopy} />
                {/* Like / Dislike buttons */}
                {record.metadata.feedbackAllowed && (
                  <>
                    <button type="button" className="btn btn-flat-secondary btn-icon" onClick={like}>
                      <SvgIcon type="thumb_up" />
                    </button>
                    <button type="button" className="btn btn-flat-secondary btn-icon" onClick={dislike}>
                      <SvgIcon type="thumb_down" />
                    </button>
                  </>
                )}
              </div>
              <button type="button" className="btn btn-flat-secondary btn-sm" onClick={contactSupport}>
                {translate('contact-us.button')}
              </button>
            </>
          )}
        </div>
      ) : showActions ? (
        <div className="sgwt-chatbot-message-actions d-flex justify-content-end gap-1">
          <CopyButton text={contentToCopy} />
          <button type="button" className="btn btn-icon btn-flat-secondary" onClick={copyToInput}>
            <SvgIcon type="replay" />
          </button>
        </div>
      ) : null}
    </section>
  );
};
